import useAuth from 'hooks/useAuth';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import handleAction from 'utils/action-manager/actionManager';
import { APIAction } from 'utils/action-manager/api/apiManager';
import { convertToJSONCustom } from 'utils/json-custom';

export type OfferItem = {
  id: string;
  type: 'development' | 'property';
  highlighted: boolean;
  name?: string;
  additional?: any;
};

type OfferContextType = {
  offer: OfferItem[];
  offerId: string | null;
  offerName: string | null;
  customerEmail: string | null;
  customerId: string | null;
  isOfferEnabled: boolean;
  isDrawerOpen: boolean;
  isEditing: boolean;
  offerUpdated: boolean;
  enableOffer: (id?: string, email?: string) => void;
  startNewOfferWithCustomerId: (id: string, email?: string) => void;
  toggleOfferDrawer: () => void;
  addItem: (item: OfferItem) => void;
  removeItem: (id: string) => void;
  toggleHighlight: (id: string) => void;
  isItemInOffer: (id: string, type: 'development' | 'property') => boolean;
  loadOfferForEditing: (offerId: string, items: OfferItem[], offerName?: string, customerEmail?: string, customerId?: string) => void;
  clearOffer: (clearName?: boolean) => void;
  saveOffer: () => void;
  getOfferItemCount: () => number;
  setOfferName: (name: string | null) => void;
  setCustomerEmail: (name: string | null) => void;
  setCustomerId: (id: string | null) => void;
  setOfferUpdated: (updated: boolean) => void;
};

// ==============================|| OFFERS - CONTEXT & PROVIDER ||============================== //

const OfferContext = createContext<OfferContextType | undefined>(undefined);

export const OfferProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [offer, setOffer] = useState<OfferItem[]>([]);
  const [isOfferEnabled, setIsOfferEnabled] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [offerUpdated, setOfferUpdated] = useState<boolean>(false);
  const [offerId, setOfferId] = useState<string | null>(null);
  const [offerName, setOfferName] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  const { user } = useAuth();

  // Initial load by local storage (context between different tabs)
  //   useEffect(() => {
  //     const savedOffer = localStorage.getItem('offer');
  //     const savedIsOfferEnabled = localStorage.getItem('isOfferEnabled');
  //     if (savedOffer) setOffer(JSON.parse(savedOffer));
  //     if (savedIsOfferEnabled) setIsOfferEnabled(JSON.parse(savedIsOfferEnabled));
  //   }, []);
  useEffect(() => {
    const savedOffer = localStorage.getItem('offer');
    const savedIsOfferEnabled = localStorage.getItem('isOfferEnabled');
    const savedOfferName = localStorage.getItem('offerName');
    const savedCustomerEmail = localStorage.getItem('customerEmail');
    const savedCustomerId = localStorage.getItem('customerId');

    const offerData = savedOffer ? JSON.parse(savedOffer) : [];
    const offerEnabled = savedIsOfferEnabled ? JSON.parse(savedIsOfferEnabled) : false;

    setOffer(offerData);
    // setIsOfferEnabled(offerData.length > 0 && offerEnabled);
    setOfferName(savedOfferName || null);
    setCustomerEmail(savedCustomerEmail || null);
    setCustomerId(savedCustomerId || null);

    if ((offerData.length > 0 || savedOfferName || savedCustomerEmail || savedCustomerId) && offerEnabled) {
      setIsOfferEnabled(true);
    } else {
      setIsOfferEnabled(false);
    }
  }, []);

  // Save offer to local storage (anytime it changes)
  //   useEffect(() => {
  //     localStorage.setItem('offer', JSON.stringify(offer));
  //     localStorage.setItem('isOfferEnabled', JSON.stringify(isOfferEnabled));
  //   }, [offer, isOfferEnabled]);
  useEffect(() => {
    if (!isInitialMount.current) {
      localStorage.setItem('offer', JSON.stringify(offer));
      localStorage.setItem('isOfferEnabled', JSON.stringify(isOfferEnabled));
      localStorage.setItem('offerName', offerName || '');
      localStorage.setItem('customerEmail', customerEmail || '');
      localStorage.setItem('customerId', customerId || '');
    } else {
      isInitialMount.current = false;
    }
  }, [offer, isOfferEnabled, offerName, customerEmail, customerId]);

  // Listener to synchronize changes between tabs
  //   useEffect(() => {
  //     const handleStorageChange = (event: StorageEvent) => {
  //       if (event.key === 'offer' && event.newValue) {
  //         const newOffer = JSON.parse(event.newValue);
  //         setOffer(newOffer);

  //         if (newOffer.length === 0) {
  //           setIsOfferEnabled(false);
  //         }
  //       }
  //       if (event.key === 'isOfferEnabled' && event.newValue) {
  //         const offerEnabled = JSON.parse(event.newValue);

  //         if (offer.length > 0 || offerEnabled === false) {
  //           setIsOfferEnabled(offerEnabled);
  //         }

  //         if (!offerEnabled) {
  //           setIsDrawerOpen(false);
  //         }
  //       }
  //       if (event.key === 'offerName' && event.newValue !== null) {
  //         setOfferName(event.newValue || null);
  //       }
  //     };

  //     window.addEventListener('storage', handleStorageChange);

  //     return () => {
  //       window.removeEventListener('storage', handleStorageChange);
  //     };
  //   }, [offer]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'offer' && event.newValue) {
        const newOffer = JSON.parse(event.newValue);
        setOffer(newOffer);
      }

      if (event.key === 'isOfferEnabled' && event.newValue) {
        const offerEnabled = JSON.parse(event.newValue);
        if (offer.length > 0 || offerName || offerEnabled === false) {
          setIsOfferEnabled(offerEnabled);
        }

        if (!offerEnabled) {
          setIsDrawerOpen(false);
        }
      }

      if (event.key === 'offerName' && event.newValue !== null) {
        setOfferName(event.newValue || null);
        if (event.newValue) {
          setIsOfferEnabled(true);
        }
      }

      if (event.key === 'customerEmail' && event.newValue !== null) {
        setCustomerEmail(event.newValue || null);
        if (event.newValue) {
          setIsOfferEnabled(true);
        }
      }

      if (event.key === 'customerId' && event.newValue !== null) {
        setCustomerId(event.newValue || null);
        if (event.newValue) {
          setIsOfferEnabled(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [offer, offerName, customerEmail, customerId]);

  const enableOffer = (id?: string, email?: string) => {
    setIsOfferEnabled(true);
    setIsEditing(false);
    setOfferId(null);
    if (id) setCustomerId(id);
    if (email) setCustomerEmail(email);
  };

  const startNewOfferWithCustomerId = (id: string, email?: string) => {
    setCustomerId(id);
    if (email) setCustomerEmail(email);
    enableOffer();
    // setIsDrawerOpen(true); // opcional: abre el drawer automáticamente
  };

  const toggleOfferDrawer = () => setIsDrawerOpen((prev) => !prev);

  const addItem = (item: OfferItem) => {
    setOffer((prevOffer) => [...prevOffer, item]);
  };

  const removeItem = (id: string) => {
    setOffer((prevOffer) => prevOffer.filter((item) => item.id !== id));
  };

  const toggleHighlight = (id: string) => {
    setOffer((prevOffer) => prevOffer.map((item) => (item.id === id ? { ...item, highlighted: !item.highlighted } : item)));
  };

  const isItemInOffer = (id: string, type: 'development' | 'property') => {
    return offer.some((item) => item.id === id && item.type === type);
  };

  const loadOfferForEditing = (offerId: string, items: OfferItem[], name?: string, customerEmail?: string, customerId?: string) => {
    setOffer(items);
    setIsOfferEnabled(true);
    setIsEditing(true);
    setOfferId(offerId);
    setOfferName(name || null);
    setCustomerEmail(customerEmail || null);
    setCustomerId(customerId || null);
    toggleOfferDrawer();
  };

  //   const clearOffer = () => {
  //     setOffer([]);
  //     setIsOfferEnabled(false);
  //     setIsDrawerOpen(false);
  //     setIsEditing(false);
  //     setOfferId(null);
  //     setOfferName(null);
  //   };

  const clearOffer = (clearName: boolean = false) => {
    setOffer([]);
    setIsOfferEnabled(false);
    setIsDrawerOpen(false);
    setIsEditing(false);
    setOfferId(null);

    if (clearName) {
      setOfferName(null);
      setCustomerEmail(null);
      setCustomerId(null);
    }
  };

  const saveOffer = () => {
    if (isEditing && offerId) {
      // Update offer
      const data = {
        // status: 'active',
        name: offerName,
        customeremail: customerEmail,
        customeridv2: customerId,
        // [ TO-DO ] - Make dynamic switch between developments and properties (when properties are included)
        developments: offer,
        // properties: offer,
        updatedate: new Date().toISOString()
      };

      const apiAction: APIAction = {
        type: 'api',
        entity: 'offers',
        method: 'update',
        data: convertToJSONCustom(data),
        id: offerId
      };

      handleAction(apiAction)
        .then((res) => {
          //   console.log(res);
          dispatch(
            openSnackbar({
              open: true,
              message: 'Offer updated successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: false,
              setTimeout: 10
            })
          );
          setOfferUpdated(true);
          clearOffer(true);
        })
        .catch((err) => console.error(err));
    } else {
      // Create offer
      const data = {
        status: 'active',
        name: offerName,
        customeremail: customerEmail,
        customeridv2: customerId,
        // [ TO-DO ] - Make dynamic switch between developments and properties (when properties are included)
        developments: offer,
        // properties: offer,
        agentid: user?.id,
        creationdate: new Date().toISOString(),
        updatedate: new Date().toISOString()
      };

      const apiAction: APIAction = {
        type: 'api',
        entity: 'offers',
        method: 'create',
        data: convertToJSONCustom(data)
      };

      handleAction(apiAction)
        .then((res) => {
          //   console.log(res);
          dispatch(
            openSnackbar({
              open: true,
              message: 'Offer created successfully.',
              variant: 'alert',
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              alert: {
                color: 'success'
              },
              close: false,
              setTimeout: 10
            })
          );
          setOfferUpdated(true);
          clearOffer(true);
        })
        .catch((err) => console.error(err));

      //   console.log(apiAction);
    }
  };

  const getOfferItemCount = () => {
    return offer.length;
  };

  return (
    <OfferContext.Provider
      value={{
        offer,
        offerName,
        offerId,
        customerEmail,
        customerId,
        isOfferEnabled,
        isDrawerOpen,
        isEditing,
        offerUpdated,
        enableOffer,
        startNewOfferWithCustomerId,
        toggleOfferDrawer,
        addItem,
        removeItem,
        toggleHighlight,
        isItemInOffer,
        loadOfferForEditing,
        clearOffer,
        saveOffer,
        getOfferItemCount,
        setOfferName,
        setCustomerEmail,
        setCustomerId,
        setOfferUpdated
      }}
    >
      {children}
    </OfferContext.Provider>
  );
};

export const useOffer = () => {
  const context = useContext(OfferContext);
  if (!context) throw new Error('context must be use inside provider');
  return context;
};
